import React, { useState, useEffect } from "react";
import {
  X,
  Send,
  Loader,
  DollarSign,
  Users,
  MapPin,
  Clock,
  MessageCircle,
  User,
  CheckCircle2,
  Briefcase,
  FileText,
  Building,
  Globe,
  Mail,
  Phone,
  Link,
  TrendingUp,
} from "lucide-react";
import InlineEditField from "./InlineEditField";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Box,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "./Modal.css";

// Utility functions for date handling
const formatDateForDisplay = (dateValue) => {
  if (!dateValue) return null;

  // If it's already a dayjs object, just return it
  if (dayjs.isDayjs(dateValue)) {
    return dateValue.startOf("day");
  }

  // If it's a string or Date object, convert to dayjs and strip time
  try {
    const parsed = dayjs(dateValue);
    return parsed.isValid() ? parsed.startOf("day") : null;
  } catch (error) {
    console.error("Error parsing date:", error);
    return null;
  }
};

const formatDateForStorage = (dayjsValue) => {
  if (!dayjsValue || !dayjsValue.isValid()) return "";

  // Format as MM/DD/YYYY for storage
  return dayjsValue.format("MM/DD/YYYY");
};

const ResourceForm = ({
  onSubmit,
  loading,
  showSuccess,
  successMessage,
  contactId,
  portalId,
}) => {
  const [formData, setFormData] = useState({
    projectTitle: "",
    solutionsServices: "",
    positionTitle: "",
    assignmentType: "", // Changed from engagementType
    contractDuration: "",
    monthlyPrice: "",
    numberOfResources: "", // Changed from resourceCount
    requiredSkills: "",
    optionalSkills: "",
    experienceLevel: "",
    workLocation: "",
    shiftTiming: "",
    levelOfCommunication: "",
    expectedJoiningDate: "",
    clientInterview: "",
    interviewProcess: "",
    jobDescription: "",
    additionalComments: "",
  });

  // File upload state for job description
  const [jobDescriptionFile, setJobDescriptionFile] = useState(null);
  const [fileUploadError, setFileUploadError] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const [contactData, setContactData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    website: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zip: "",
    industry: "",
    numberOfEmployees: "",
    linkedinProfile: "",
    linkedinCompanyPage: "",
    facebookCompanyPage: "",
    twitterHandle: "",
    hubspotOwnerId: "",
    note: "",
    conferenceName: "",
    initialEmailResponse: "",
    linkedinUrl: "",
    companyDescription: "",
    companyKeywords: "",
    companySpecialities: "",
    foundedYear: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [loadingContact, setLoadingContact] = useState(false);
  const [contactError, setContactError] = useState("");
  const [extractedContactId, setExtractedContactId] = useState(null);
  const [extractedPortalId, setExtractedPortalId] = useState(null);

  // Deal selection state
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState("");
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [dealError, setDealError] = useState("");
  const [dealStage, setDealStage] = useState("2638137"); // Default to "Estimate Requested" for Resource Modal

  // Function to extract contact ID from HubSpot URL
  const extractContactIdFromUrl = (url) => {
    try {
      const hubspotUrlPattern = /\/contacts\/\d+\/record\/0-1\/(\d+)/;
      const match = url.match(hubspotUrlPattern);
      if (match) {
        return match[1]; // Return the contact ID (751 in the example)
      }

      // Handle direct contact ID in URL parameters
      const urlObj = new URL(url);
      const contactIdFromParams = urlObj.searchParams.get("contactId");
      if (contactIdFromParams) {
        return contactIdFromParams;
      }

      return null;
    } catch (error) {
      console.error("Error extracting contact ID from URL:", error);
      return null;
    }
  };

  // Extract URL parameters and HubSpot URLs when component mounts
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlContactId = params.get("contactId");
    const urlPortalId = params.get("portalId");
    const componentName = params.get("componentName");
    const hubspotUrl = params.get("hubspotUrl");

    let extractedContactId = urlContactId;

    // Try to extract contact ID from HubSpot URL if provided
    if (hubspotUrl) {
      const idFromUrl = extractContactIdFromUrl(hubspotUrl);
      if (idFromUrl) {
        extractedContactId = idFromUrl;
      }
    }

    // Also try to extract from the current page URL if it's a HubSpot URL
    if (!extractedContactId) {
      const currentUrl = window.location.href;
      const idFromCurrentUrl = extractContactIdFromUrl(currentUrl);
      if (idFromCurrentUrl) {
        extractedContactId = idFromCurrentUrl;
      }
    }

    // Store extracted IDs in state
    if (extractedContactId) {
      setExtractedContactId(extractedContactId);
      console.log(
        "DEBUG ResourceModal: Extracted contactId:",
        extractedContactId
      );
    }
    if (urlPortalId) {
      setExtractedPortalId(urlPortalId);
      console.log("DEBUG ResourceModal: Extracted portalId:", urlPortalId);
    }

    // Only proceed if componentName matches or we have a contact ID
    if (
      (componentName === "requestResource" || extractedContactId) &&
      extractedContactId
    ) {
      // If we extracted a contact ID and it's different from the prop, trigger fetch
      if (extractedContactId !== contactId && extractedContactId) {
        // Store extracted contact ID for use
        setLoadingContact(true);
        fetchContactData(extractedContactId);
      }
    }
  }, [contactId]);

  // Fetch contact data when component mounts or contactId changes
  useEffect(() => {
    if (contactId) {
      fetchContactData(contactId);
      fetchDeals(contactId);
    }
    // Always fetch deal stages and dynamic properties when modal opens
    fetchDealStages();
    fetchDynamicProperties();
  }, [contactId]);

  // Fetch deals when extractedContactId changes
  useEffect(() => {
    if (extractedContactId) {
      fetchDeals(extractedContactId);
    }
  }, [extractedContactId]);

  // Dynamic dropdown properties state
  const [dynamicDropdowns, setDynamicDropdowns] = useState({
    experience_level: [],
    communication_requirements: [],
    work_location: [],
    shift_timing: [],
  });

  // Updated fetchContactData function
  const fetchContactData = async (id) => {
    setLoadingContact(true);
    setContactError("");

    try {
      // Get API URL from environment variable
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      console.log(
        "DEBUG ResourceModal: REACT_APP_API_URL from env:",
        process.env.REACT_APP_API_URL
      );
      console.log("DEBUG ResourceModal: Using apiUrl:", apiUrl);
      console.log(
        `DEBUG ResourceModal: Full fetch URL: ${apiUrl}/api/hubspot/contact/${id}`
      );

      const response = await fetch(`${apiUrl}/api/hubspot/contact/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        // Add credentials and CORS handling
        credentials: "omit",
        mode: "cors",
      });

      console.log(`API Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error Response:`, errorText);
        throw new Error(
          `HTTP error! Status: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();

      if (data.success) {
        // Map the contact data from backend response with ALL available fields
        const mappedContactData = {
          // Basic Info
          firstName: data.contact.firstname || "",
          lastName: data.contact.lastname || "",
          email: data.contact.email || "",
          phone: data.contact.phone || "",
          mobilephone: data.contact.mobilephone || "",

          // Company Info
          company: data.contact.company || "",
          jobTitle: data.contact.job_title || data.contact.jobtitle || "",
          industry: data.contact.industry || "",

          // Location - handle multiple location sources
          address: data.contact.address || "",
          city: data.contact.city || data.contact.hs_ip_city || "",
          state: data.contact.state || data.contact.hs_ip_state || "",
          country: data.contact.country || data.contact.hs_ip_country || "",
          zip: data.contact.zip || "",

          // Web Presence
          website: data.contact.website || "",
          linkedinProfile: data.contact.linkedin_profile || "",
          linkedinUrl: data.contact.linkedin_url || "",
          linkedinBio: data.contact.linkedinbio || "",
          linkedinCompanyPage: data.contact.linkedin_company_page || "",
          facebookCompanyPage: data.contact.facebook_company_page || "",
          twitterHandle:
            data.contact.twitter_handle || data.contact.twitterhandle || "",

          // Company Details
          numberOfEmployees:
            data.contact.number_of_employees ||
            data.contact.num_employees ||
            data.contact.numemployees ||
            "",
          companySize:
            data.contact.company_size || data.contact.companysize || "",
          annualRevenue: data.contact.annualrevenue || "",
          foundedYear:
            data.contact.founded_year || data.contact.foundedyear || "",
          companyDescription: data.contact.description || "",
          companyKeywords: data.contact.hs_keywords || "",
          companySpecialities: data.contact.company_specialities || "",
          linkedinDescription: data.contact.linkedin_description || "",

          // Business Info
          technologyStack: data.contact.technology_stack || "",
          engagementType: data.contact.engagement_type || "",
          solutions: data.contact.solutions || "",
          campaign: data.contact.campaign || "",
          category: data.contact.category || "",

          // Notes & Communication
          note: data.contact.note || "",
          conferenceName: data.contact.conference_name || "",
          initialEmailResponse: data.contact.initial_email_response || "",

          // HubSpot Metadata
          hubspotOwnerId: data.contact.hubspot_owner_id || "",
          objectId: data.contact.hs_object_id || "",
          createDate: data.contact.createdate || "",
          lastModifiedDate: data.contact.lastmodifieddate || "",
          leadStatus: data.contact.hs_lead_status || "",
          lifecycleStage: data.contact.lifecyclestage || "",

          // Additional fields
          emailDomain: data.contact.hs_email_domain || "",
          persona: data.contact.hs_persona || "",
          timeZone:
            data.contact.hs_time_zone || data.contact.hs_ip_timezone || "",
          language: data.contact.hs_language || "",

          // Metadata from backend
          contactId: data.metadata?.contactId || "",
          portalId: data.metadata?.portalId || "",
          hubspotContactUrl: data.metadata?.hubspotContactUrl || "",
        };

        // Use the mapped contact data
        setContactData(mappedContactData);

        // Pre-fill some form fields based on contact data
        const clientName =
          `${mappedContactData.firstName} ${mappedContactData.lastName}`.trim();
        const projectTitleBase =
          mappedContactData.company || clientName || "Client";

        setFormData((prev) => ({
          ...prev,
          projectTitle: `${projectTitleBase} - Resource Request`,
          solutionsServices:
            mappedContactData.solutions ||
            "Staffing and Remote Resource Management",
          assignmentType: mappedContactData.assignmentType || "",
        }));

        console.log("Contact data loaded successfully:", mappedContactData);
      } else {
        // Handle error
        console.error("API error:", data.message);
        setContactError(data.message || "Failed to fetch contact data");
      }
    } catch (error) {
      console.error("Error fetching contact data:", error);
      setContactError("Error fetching contact data: " + error.message);
    } finally {
      setLoadingContact(false);
    }
  };

  // Get list of all required fields (excluding additionalComments)
  const getRequiredFields = () => {
    const allFields = Object.keys(formData);
    const contactFields = [
      "clientName",
      "companyName",
      "companyWebsite",
      "emailAddress",
      "clientLocation",
    ];
    const dealFields = ["selectedDeal"];

    // Combine all field types
    const requiredFields = [];

    // Add form data fields
    allFields.forEach((field) => {
      // Always exclude additionalComments and optionalSkills
      if (field === "additionalComments" || field === "optionalSkills") return;

      // Show interview process fields for all deals (both new and existing)
      // Remove the conditional exclusion - interview fields are always required
      requiredFields.push(field);
    });

    // Add contact fields
    requiredFields.push(...contactFields);

    // Add deal selection field
    requiredFields.push(...dealFields);

    return requiredFields;
  };

  // Generate validation summary
  const getValidationSummary = () => {
    const requiredFields = getRequiredFields();

    // Enhanced console logging for required fields
    console.log("🔍 DEBUG - getValidationSummary called");
    console.log("📋 Required fields list:", requiredFields);
    console.log("📝 Current form data:", formData);
    console.log("👤 Current contact data:", contactData);
    console.log("🎯 Selected deal ID:", selectedDealId);
    console.log("🎯 Deal stage:", dealStage);

    const fieldLabels = {
      // Form fields
      projectTitle: "Project Title",
      solutionsServices: "Solutions/Services",
      positionTitle: "Position Title",
      assignmentType: "Assignment Type",
      contractDuration: "Contract Duration",
      monthlyPrice: "Monthly Price",
      numberOfResources: "Number of Resources",
      requiredSkills: "Required Skills",
      experienceLevel: "Experience Level",
      workLocation: "Work Location",
      shiftTiming: "Shift Timing",
      levelOfCommunication: "Communication Level",
      expectedJoiningDate: "Close Date",
      clientInterview: "Client Interview",
      interviewProcess: "Interview Process",
      jobDescription: "Job Description",
      // Contact fields
      clientName: "Client Name",
      companyName: "Company Name",
      companyWebsite: "Company Website",
      emailAddress: "Email Address",
      clientLocation: "Client Location",
      // Deal selection
      selectedDeal: "Deal Selection",
    };

    const summary = {
      total: requiredFields.length,
      filled: 0,
      missing: [],
      completed: [],
    };

    console.log("🔍 Processing each required field:");

    requiredFields.forEach((field) => {
      let value;
      let isFilled = false;

      // Handle different data sources
      if (field === "selectedDeal") {
        value = selectedDealId;
        isFilled = value && value.toString().trim() !== "";
        console.log(
          `   📊 Field: ${field} | Value: "${value}" | Filled: ${isFilled}`
        );
      } else if (field === "clientName") {
        value = `${contactData.firstName || ""} ${
          contactData.lastName || ""
        }`.trim();
        isFilled = value.length > 0;
        console.log(
          `   👤 Field: ${field} | Value: "${value}" | Filled: ${isFilled}`
        );
      } else if (field === "companyName") {
        value = contactData.company;
        isFilled = value && value.toString().trim() !== "";
        console.log(
          `   🏢 Field: ${field} | Value: "${value}" | Filled: ${isFilled}`
        );
      } else if (field === "companyWebsite") {
        value = contactData.website;
        isFilled = value && value.toString().trim() !== "";
        console.log(
          `   🌐 Field: ${field} | Value: "${value}" | Filled: ${isFilled}`
        );
      } else if (field === "emailAddress") {
        value = contactData.email;
        isFilled = value && value.toString().trim() !== "";
        console.log(
          `   📧 Field: ${field} | Value: "${value}" | Filled: ${isFilled}`
        );
      } else if (field === "clientLocation") {
        const locationParts = [
          contactData.city,
          contactData.state,
          contactData.country,
        ].filter(Boolean);
        value = locationParts.join(", ");
        isFilled = locationParts.length > 0;
        console.log(
          `   📍 Field: ${field} | Location parts: [${locationParts.join(
            ", "
          )}] | Value: "${value}" | Filled: ${isFilled}`
        );
      } else {
        // Regular form fields
        value = formData[field];

        // Special handling for jobDescription - consider file upload
        if (field === "jobDescription") {
          isFilled =
            (value && value.toString().trim() !== "") || jobDescriptionFile;
          console.log(
            `   📝 Field: ${field} | Value: "${value}" | File: ${
              jobDescriptionFile ? jobDescriptionFile.name : "none"
            } | Filled: ${isFilled}`
          );
        } else {
          isFilled = value && value.toString().trim() !== "";
          console.log(
            `   📝 Field: ${field} | Value: "${value}" | Filled: ${isFilled}`
          );
        }

        // Additional validation check for form fields
        const validationError = validateField(field, value || "");
        if (validationError) {
          console.log(
            `   ⚠️  Field: ${field} has validation error: "${validationError}"`
          );
        }
      }

      if (isFilled) {
        summary.filled++;
        summary.completed.push(fieldLabels[field] || field);
      } else {
        summary.missing.push(fieldLabels[field] || field);
        console.log(
          `   ❌ Field: ${field} (${fieldLabels[field] || field}) is MISSING`
        );
      }
    });

    console.log("📊 FINAL VALIDATION SUMMARY:");
    console.log(`   Total fields: ${summary.total}`);
    console.log(`   Filled fields: ${summary.filled}`);
    console.log(
      `   Missing fields (${summary.missing.length}):`,
      summary.missing
    );
    console.log(
      `   Completed fields (${summary.completed.length}):`,
      summary.completed
    );
    console.log(`   Is form valid: ${summary.missing.length === 0}`);

    return summary;
  };

  // Form validation rules
  const validateField = (name, value) => {
    switch (name) {
      case "projectTitle":
        if (!value || !value.trim()) return "Project title is required";
        if (value.length < 3)
          return "Project title must be at least 3 characters";
        return "";

      case "solutionsServices":
        if (!value || !value.trim()) return "Solutions/Services is required";
        return "";

      case "positionTitle":
        if (!value || !value.trim()) return "Position title is required";
        if (value.length < 3)
          return "Position title must be at least 3 characters";
        return "";

      case "assignmentType":
        if (!value) return "Assignment type is required";
        return "";

      case "contractDuration":
        if (!value) return "Contract duration is required";
        return "";

      case "monthlyPrice":
        if (!value) return "Monthly price is required";
        if (isNaN(value) || parseFloat(value) <= 0)
          return "Please enter a valid price";
        if (parseFloat(value) < 100) return "Minimum price is $100";
        return "";

      case "numberOfResources":
        if (!value) return "Number of resources is required";
        return "";

      case "experienceLevel":
        if (!value) return "Experience level is required";
        return "";

      case "requiredSkills":
        if (!value || !value.trim()) return "Required skills are mandatory";
        if (value.length < 10)
          return "Please provide more detailed skills (minimum 10 characters)";
        return "";

      case "workLocation":
        if (!value) return "Work location is required";
        return "";

      case "shiftTiming":
        if (!value) return "Shift timing is required";
        return "";

      case "levelOfCommunication":
        if (!value) return "Communication level is required";
        return "";

      case "expectedJoiningDate":
        if (!value || !value.trim()) return "Close date is required";
        return "";

      case "clientInterview":
        if (!value) return "Please specify client interview preference";
        return "";

      case "interviewProcess":
        if (!value || !value.trim())
          return "Interview process details are required";
        if (value.length < 5)
          return "Please provide more details about the interview process";
        return "";

      case "jobDescription":
        // Job description is filled if either text OR file is provided
        if ((!value || !value.trim()) && !jobDescriptionFile) {
          return "Job description is required (text or file)";
        }
        if (value && value.trim() && value.length < 20) {
          return "Please provide a more detailed job description";
        }
        return "";

      // Client detail validations (basic required validations)
      case "clientName":
        const clientName = `${contactData.firstName || ""} ${
          contactData.lastName || ""
        }`.trim();
        if (!clientName) return "Client name is required";
        return "";

      case "companyName":
        if (!contactData.company || !contactData.company.trim())
          return "Company name is required";
        return "";

      case "companyWebsite":
        if (!contactData.website || !contactData.website.trim())
          return "Company website is required";
        return "";

      case "emailAddress":
        if (!contactData.email || !contactData.email.trim())
          return "Email address is required";
        return "";

      case "clientLocation":
        const locationParts = [
          contactData.city,
          contactData.state,
          contactData.country,
        ].filter(Boolean);
        if (locationParts.length === 0) return "Client location is required";
        return "";

      default:
        return "";
    }
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = getRequiredFields();

    requiredFields.forEach((key) => {
      // Special handling for jobDescription - consider file upload
      if (key === "jobDescription") {
        // If file is uploaded, skip validation error
        if (jobDescriptionFile) {
          // Field is filled via file upload
          return;
        }
      }

      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check form validity on data change AND file upload
  useEffect(() => {
    const isValid = validateForm();
    setIsFormValid(isValid);
  }, [formData, jobDescriptionFile]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedFormData = { ...formData, [name]: value };

    setFormData(updatedFormData);

    // Mark field as touched
    setTouched({
      ...touched,
      [name]: true,
    });

    // Validate field on change
    const error = validateField(name, value);
    setErrors({
      ...errors,
      [name]: error,
    });
  };

  // Handle file upload for job description
  const handleFileUpload = (file) => {
    if (!file) return;

    // Validate file type (PDF only)
    if (file.type !== "application/pdf") {
      setFileUploadError("Only PDF files are allowed");
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setFileUploadError("File size must be less than 10MB");
      return;
    }

    setJobDescriptionFile(file);
    setFileUploadError("");

    // Mark field as touched and CLEAR the error since file is uploaded
    setTouched({ ...touched, jobDescription: true });
    // Clear the jobDescription error since file is now uploaded
    setErrors({ ...errors, jobDescription: "" });
  };

  // Handle file selection via input
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Handle drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Remove uploaded file
  const handleRemoveFile = () => {
    setJobDescriptionFile(null);
    setFileUploadError("");

    // Re-validate the field
    const error = validateField("jobDescription", formData.jobDescription);
    setErrors({ ...errors, jobDescription: error });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Auto-append company name to project title when user finishes typing
    if (name === "projectTitle" && contactData.company && value.trim()) {
      const companySuffix = ` - ${contactData.company}`;
      // Only append if the value doesn't already contain the company suffix
      if (!value.includes(companySuffix)) {
        const updatedFormData = {
          ...formData,
          projectTitle: value + companySuffix,
        };
        setFormData(updatedFormData);
      }
    }

    setTouched({
      ...touched,
      [name]: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("🚀 SEND REQUEST BUTTON CLICKED!");
    console.log("🔍 Button click validation check starting...");

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Get validation summary
    console.log("📊 Getting validation summary on button click...");
    const validationSummary = getValidationSummary();

    console.log("📋 BUTTON CLICK VALIDATION RESULTS:");
    console.log(`   Missing fields count: ${validationSummary.missing.length}`);
    console.log(`   Missing fields:`, validationSummary.missing);
    console.log(`   Total fields: ${validationSummary.total}`);
    console.log(`   Filled fields: ${validationSummary.filled}`);

    // If there are missing required fields, show validation summary
    if (validationSummary.missing.length > 0) {
      setShowValidationSummary(true);
      setActiveStep(0); // Go back to first step
      return;
    }

    // Final validation for detailed errors
    if (!validateForm()) {
      setShowValidationSummary(true);
      setActiveStep(0); // Go back to first step if there are errors
      return;
    }

    // Hide validation summary if form is valid
    setShowValidationSummary(false);

    try {
      // Use the selected deal stage from the dropdown
      const submissionData = {
        modalType: "RESOURCE", // Explicitly specify this is a Resource Modal update
        contactId: contactId || extractedContactId,
        portalId,
        contactData,
        formData: {
          ...formData,
          // Add the fields that will be used in both resource and contract forms
          contract_duration: formData.contractDuration,
          services: formData.solutionsServices,
          // Map form fields to HubSpot property names
          position_title: formData.positionTitle,
          monthly_price: formData.monthlyPrice,
          required_skills: formData.requiredSkills,
          experience_level: formData.experienceLevel,
          communication_requirements: formData.levelOfCommunication,
          work_location: formData.workLocation,
          shift_timing: formData.shiftTiming,
          // Convert MM/DD/YYYY date to HubSpot datetime format (milliseconds)
          closedate: formData.expectedJoiningDate
            ? dayjs(formData.expectedJoiningDate, "MM/DD/YYYY")
                .startOf("day")
                .valueOf()
            : null,
        },
        // Use the deal stage selected in the dropdown
        dealStage: dealStage, // Use the current dealStage from the dropdown
        selectedDealId: selectedDealId,
        updateExistingDeal: selectedDeal ? true : false,
      };

      // If there's a file, use FormData, otherwise send JSON directly
      if (jobDescriptionFile) {
        console.log("📎 File present - sending as FormData");
        const submissionFormData = new FormData();
        submissionFormData.append("jobDescriptionFile", jobDescriptionFile);
        submissionFormData.append("data", JSON.stringify(submissionData));
        onSubmit(submissionFormData, jobDescriptionFile);
      } else {
        console.log("📝 No file - sending as JSON");
        onSubmit(submissionData);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      // Handle any form submission errors here if needed
    }
  };

  // Helper function to get field error
  const getFieldError = (fieldName) => {
    return touched[fieldName] && errors[fieldName] ? errors[fieldName] : "";
  };

  // Progress calculation
  const getProgress = () => {
    const totalFields = Object.keys(formData).length - 2; // Exclude optionalSkills and additionalComments
    const filledFields = Object.keys(formData).filter(
      (key) =>
        key !== "optionalSkills" &&
        key !== "additionalComments" &&
        formData[key] &&
        formData[key].toString().trim()
    ).length;
    return Math.round((filledFields / totalFields) * 100);
  };

  // Reset form after successful submission
  const resetForm = () => {
    setFormData({
      projectTitle: "",
      solutionsServices: "",
      positionTitle: "",
      assignmentType: "",
      contractDuration: "",
      monthlyPrice: "",
      numberOfResources: "",
      requiredSkills: "",
      optionalSkills: "",
      experienceLevel: "",
      workLocation: "",
      shiftTiming: "",
      levelOfCommunication: "",
      expectedJoiningDate: "",
      clientInterview: "",
      interviewProcess: "",
      jobDescription: "",
      additionalComments: "",
    });
    setErrors({});
    setTouched({});
    setActiveStep(0);
  };

  // Function to update all contact data
  const updateContactData = async () => {
    try {
      let currentContactId = contactId || extractedContactId;

      if (!currentContactId) {
        if (contactData.contactId) {
          currentContactId = contactData.contactId;
        } else {
          const params = new URLSearchParams(window.location.search);
          const urlContactId = params.get("contactId");
          const hubspotUrl = params.get("hubspotUrl");

          if (urlContactId) {
            currentContactId = urlContactId;
          } else if (hubspotUrl) {
            currentContactId = extractContactIdFromUrl(hubspotUrl);
          }
        }
      }

      if (!currentContactId) {
        throw new Error("No contact ID available for update.");
      }

      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      // Prepare properties to update - only send non-empty values
      const properties = {};
      if (contactData.firstName) properties.firstname = contactData.firstName;
      if (contactData.lastName) properties.lastname = contactData.lastName;
      if (contactData.company) properties.company = contactData.company;
      if (contactData.website) properties.website = contactData.website;
      if (contactData.email) properties.email = contactData.email;
      if (contactData.city) properties.city = contactData.city;
      if (contactData.state) properties.state = contactData.state;
      if (contactData.country) properties.country = contactData.country;

      console.log("Updating contact with properties:", properties);

      const response = await fetch(
        `${apiUrl}/api/hubspot/contact/${currentContactId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "omit",
          mode: "cors",
          body: JSON.stringify({ properties }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! Status: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Contact update failed");
      }

      console.log("Successfully updated contact data");
    } catch (error) {
      console.error("Error updating contact data:", error);
      throw error;
    }
  };

  // Function to update contact information
  const updateContactInfo = async (field, value) => {
    try {
      // Get the correct contact ID
      let currentContactId = contactId;

      // If contactId prop is not available, try to extract from contactData or URL
      if (!currentContactId) {
        if (contactData.contactId) {
          currentContactId = contactData.contactId;
        } else {
          // Try to extract from URL parameters
          const params = new URLSearchParams(window.location.search);
          const urlContactId = params.get("contactId");
          const hubspotUrl = params.get("hubspotUrl");

          if (urlContactId) {
            currentContactId = urlContactId;
          } else if (hubspotUrl) {
            currentContactId = extractContactIdFromUrl(hubspotUrl);
          }
        }
      }

      // Validate that we have a contact ID
      if (!currentContactId) {
        throw new Error(
          "No contact ID available for update. Please ensure contact data is loaded."
        );
      }

      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      // Map frontend field names to HubSpot property names
      const fieldMapping = {
        firstName: "firstname",
        lastName: "lastname",
        company: "company",
        website: "website",
        email: "email",
        city: "city",
        state: "state",
        country: "country",
      };

      const hubspotField = fieldMapping[field] || field;
      const properties = { [hubspotField]: value };

      console.log(
        `Updating contact ${currentContactId} field ${hubspotField} with value:`,
        value
      );

      const response = await fetch(
        `${apiUrl}/api/hubspot/contact/${currentContactId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "omit",
          mode: "cors",
          body: JSON.stringify({ properties }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! Status: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();

      if (data.success) {
        // Update local state with new value
        setContactData((prev) => ({
          ...prev,
          [field]: value,
        }));
        console.log(`Successfully updated ${field} to:`, value);
      } else {
        throw new Error(data.message || "Update failed");
      }
    } catch (error) {
      console.error(`Error updating contact ${field}:`, error);
      throw error; // Re-throw to show error in UI
    }
  };

  // Function to retry fetching contact data
  const retryFetchContactData = () => {
    if (contactId) {
      fetchContactData(contactId);
    }
  };

  // Fetch deals for the contact
  const fetchDeals = async (id) => {
    setLoadingDeals(true);
    setDealError("");

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const response = await fetch(
        `${apiUrl}/api/hubspot/deals/contact/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "omit",
          mode: "cors",
        }
      );

      const data = await response.json();

      if (data.success) {
        setDeals(data.deals || []);
      } else {
        setDealError(data.message || "Failed to fetch deals");
      }
    } catch (err) {
      console.error("Error fetching deals:", err);
      setDealError("Error fetching deals: " + err.message);
    } finally {
      setLoadingDeals(false);
    }
  };

  // Handle deal selection
  const handleDealSelect = (dealId) => {
    setSelectedDealId(dealId);

    if (dealId === "new") {
      // Creating new deal - reset ALL fields to empty
      setSelectedDeal(null);

      // Find and set "Estimate Requested" stage for new deals in Resource Modal
      const estimateRequestedStage = dealStages.find(
        (stage) =>
          stage.id === "2638137" || stage.label === "Estimate Requested"
      );
      if (estimateRequestedStage) {
        setDealStage(estimateRequestedStage.id);
        console.log(
          "🎯 Resource Modal: New deal - stage set to Estimate Requested:",
          estimateRequestedStage.id
        );
      } else {
        setDealStage("2638137"); // Fallback to static value
        console.warn(
          "⚠️ Estimate Requested stage not found in dealStages, using fallback"
        );
      }

      // Reset ALL form fields to empty for new deal
      setFormData({
        projectTitle: "",
        solutionsServices: "",
        positionTitle: "",
        assignmentType: "",
        contractDuration: "",
        monthlyPrice: "",
        numberOfResources: "",
        requiredSkills: "",
        optionalSkills: "",
        experienceLevel: "",
        workLocation: "",
        shiftTiming: "",
        levelOfCommunication: "",
        expectedJoiningDate: "",
        clientInterview: "",
        interviewProcess: "",
        jobDescription: "",
        additionalComments: "",
      });

      // Keep contact data populated - do NOT clear client information section
    } else if (dealId) {
      // Editing existing deal - map HubSpot deal data to form fields
      const deal = deals.find((d) => d.id === dealId);
      if (deal) {
        setSelectedDeal(deal);
        // Use the dealstage from properties, not the stage field
        setDealStage(deal.properties?.dealstage || deal.stage || "2638137");
        console.log(
          "🎯 Resource Modal: Selected deal stage from properties:",
          deal.properties?.dealstage || deal.stage
        );

        // Map HubSpot deal data to existing form fields
        console.log("🔧 Mapping HubSpot deal data to resource form:", deal);
        setFormData((prevData) => ({
          ...prevData,
          // Map deal data to form fields (check both direct properties and nested properties)
          projectTitle: deal.properties?.dealname || "",
          monthlyPrice: deal.properties?.monthly_price?.toString() || "",
          solutionsServices: deal.services || "",
          assignmentType:
            deal.properties?.assignment_type || prevData.assignmentType,
          expectedJoiningDate: deal.closeDate
            ? dayjs(deal.closeDate).format("MM/DD/YYYY")
            : deal.properties?.closedate
            ? dayjs(deal.properties.closedate).format("MM/DD/YYYY")
            : prevData.expectedJoiningDate,
          // Map additional deal properties with correct HubSpot field names
          positionTitle:
            deal.properties?.position_title || prevData.positionTitle,
          contractDuration:
            deal.properties?.contract_duration || prevData.contractDuration,
          numberOfResources: deal.properties?.number_of_resources,
          requiredSkills: deal.properties?.required_skills,
          optionalSkills:
            deal.properties?.optional_skills || prevData.optionalSkills,
          experienceLevel: deal.properties?.experience_level,
          workLocation: deal.properties?.work_location,
          shiftTiming: deal.properties?.shift_timing,
          levelOfCommunication: deal.properties?.communication_requirements,
          clientInterview:
            deal.properties?.hs_client_interview || prevData.clientInterview,
          interviewProcess:
            deal.properties?.hs_interview_process || prevData.interviewProcess,
          jobDescription:
            deal.properties?.hs_job_description || prevData.jobDescription,
        }));

        console.log("✅ Resource form pre-filled with HubSpot deal data");
      }
    } else {
      // No selection - reset to default state
      setSelectedDeal(null);

      // Find and set "Estimate Requested" stage as default for Resource Modal
      const estimateRequestedStage = dealStages.find(
        (stage) =>
          stage.id === "2638137" || stage.label === "Estimate Requested"
      );
      if (estimateRequestedStage) {
        setDealStage(estimateRequestedStage.id);
      } else {
        setDealStage("2638137"); // Fallback to static value
      }
    }
  };

  // Deal stages state - will be populated from API
  const [dealStages, setDealStages] = useState([]);
  const [loadingDealStages, setLoadingDealStages] = useState(false);

  // Dynamic dropdown properties state
  const [dynamicProperties, setDynamicProperties] = useState({});
  const [loadingProperties, setLoadingProperties] = useState(false);

  // Fetch dynamic properties from HubSpot
  const fetchDynamicProperties = async () => {
    setLoadingProperties(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const keys =
        "engagement_type,contract_duration,number_of_resources,assignment_type,pricing_terms,experience_level,communication_requirements,work_location,shift_timing";

      const response = await fetch(
        `${apiUrl}/api/hubspot/deal-properties?keys=${keys}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "omit",
          mode: "cors",
        }
      );

      const data = await response.json();
      if (data.success && data.properties) {
        // Convert array of properties to object with property names as keys
        const propertiesObj = {};
        data.properties.forEach((prop) => {
          propertiesObj[prop.name] = prop;
        });
        setDynamicProperties(propertiesObj);

        // Update dynamic dropdown options
        const newDynamicDropdowns = {};
        [
          "experience_level",
          "communication_requirements",
          "work_location",
          "shift_timing",
        ].forEach((key) => {
          if (propertiesObj[key] && propertiesObj[key].options) {
            newDynamicDropdowns[key] = propertiesObj[key].options;
          } else {
            newDynamicDropdowns[key] = [];
          }
        });
        setDynamicDropdowns(newDynamicDropdowns);

        console.log(
          "✅ Dynamic properties loaded from HubSpot:",
          propertiesObj
        );
        console.log("✅ Dynamic dropdowns populated:", newDynamicDropdowns);
      } else {
        console.warn(
          "⚠️ Failed to load dynamic properties from HubSpot, using static fallback"
        );
      }
    } catch (error) {
      console.error("Error fetching dynamic properties:", error);
      // Keep static dropdown options if API fails
    } finally {
      setLoadingProperties(false);
    }
  };

  // Fetch deal stages from HubSpot
  const fetchDealStages = async () => {
    setLoadingDealStages(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const response = await fetch(`${apiUrl}/api/hubspot/deal-stages`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "omit",
        mode: "cors",
      });

      const data = await response.json();
      if (data.success && data.stages) {
        // Map the stages to the format expected by the UI
        const mappedStages = data.stages.map((stage) => ({
          id: stage.id || stage.value, // Use value field if id is not present
          label: stage.label,
        }));
        setDealStages(mappedStages);
        console.log("✅ Deal stages loaded from HubSpot:", mappedStages);
        console.log(
          "🔧 Available stage IDs:",
          mappedStages.map((s) => s.id)
        );

        // Set default stage for Resource Modal to "Estimate Requested" if not already set
        if (!dealStage || dealStage === "2638137") {
          const estimateRequestedStage = mappedStages.find(
            (stage) =>
              stage.id === "2638137" || stage.label === "Estimate Requested"
          );
          if (estimateRequestedStage) {
            setDealStage(estimateRequestedStage.id);
            console.log(
              "🎯 Resource Modal: Default stage set to Estimate Requested:",
              estimateRequestedStage.id
            );
          } else {
            console.warn(
              "⚠️ Estimate Requested stage not found, keeping current default"
            );
          }
        }
      } else {
        console.warn(
          "⚠️ Failed to load deal stages from HubSpot, using fallback"
        );
      }
    } catch (error) {
      console.error("Error fetching deal stages:", error);
      // Keep fallback stages if API fails
    } finally {
      setLoadingDealStages(false);
    }
  };

  return (
    <div className="resource-form-container">
      <div className="form-content">
        {showSuccess ? (
          <div className="success-state-clean">
            <div className="success-icon-clean">
              <CheckCircle2 size={64} />
            </div>
            <h3>Request Submitted Successfully!</h3>
            <p className="success-message-clean">{successMessage}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="resource-form">
            {/* HubSpot Deal Selection Section */}
            {(contactId || extractedContactId) && (
              <div className="form-section enhanced">
                <div className="section-header">
                  <div className="section-icon">
                    <TrendingUp size={20} />
                  </div>
                  <div>
                    <h3>HubSpot Deal</h3>
                    <p>Select an existing deal to edit or create a new one</p>
                  </div>
                </div>

                {dealError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {dealError}
                  </Alert>
                )}

                <div className="form-grid">
                  {/* Deal Selection Dropdown */}
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Select Deal <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel id="deal-select-label">
                        Choose a deal or create new
                      </InputLabel>
                      <Select
                        labelId="deal-select-label"
                        value={selectedDealId}
                        onChange={(e) => handleDealSelect(e.target.value)}
                        label="Choose a deal or create new"
                        size="small"
                        disabled={loadingDeals}
                      >
                        <MenuItem value="">
                          <em>Select an option...</em>
                        </MenuItem>
                        <MenuItem value="new">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <TrendingUp size={16} />
                            <strong>Create New Deal</strong>
                          </div>
                        </MenuItem>
                        {deals.map((deal) => (
                          <MenuItem key={deal.id} value={deal.id}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>
                                {deal.name}
                              </span>
                            </div>
                          </MenuItem>
                        ))}
                      </Select>
                      {loadingDeals && (
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Loader size={16} className="animate-spin" />
                          Loading deals...
                        </Typography>
                      )}
                    </FormControl>
                  </div>

                  {/* Deal Stage Selection */}
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Deal Stage <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel id="deal-stage-label">
                        Select Stage
                      </InputLabel>
                      <Select
                        labelId="deal-stage-label"
                        value={dealStage}
                        onChange={(e) => setDealStage(e.target.value)}
                        label="Select Stage"
                        size="small"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                            },
                          },
                        }}
                      >
                        {dealStages.map((stage) => (
                          <MenuItem
                            key={stage.id || stage.value}
                            value={stage.id || stage.value}
                          >
                            {stage.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>
              </div>
            )}

            {/* Client Information Section (Auto-filled) */}
            <div className="form-section enhanced">
              <div className="section-header">
                <div className="section-icon">
                  <Building size={20} />
                </div>
                <div>
                  <h3>Client Information</h3>
                  <p>Client details (auto-filled from HubSpot)</p>
                </div>
              </div>

              {loadingContact ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading client information...
                  </Typography>
                </Box>
              ) : contactError ? (
                <Box sx={{ p: 2 }}>
                  <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={retryFetchContactData}
                      >
                        Retry
                      </Button>
                    }
                  >
                    {contactError}
                  </Alert>
                </Box>
              ) : (
                <div className="form-grid">
                  {/* Client Name - Always Editable with TextField */}
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Client Name <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      id="clientName"
                      name="clientName"
                      placeholder="Enter client name"
                      value={`${contactData.firstName || ""} ${
                        contactData.lastName || ""
                      }`.trim()}
                      onChange={(e) => {
                        const [firstName = "", ...lastNameParts] =
                          e.target.value.split(" ");
                        const lastName = lastNameParts.join(" ");
                        setContactData((prev) => ({
                          ...prev,
                          firstName,
                          lastName,
                        }));
                      }}
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <User
                            size={18}
                            style={{ marginRight: "8px", color: "#6b7280" }}
                          />
                        ),
                      }}
                    />
                  </div>

                  {/* Company Name - Always Editable with TextField */}
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Company Name <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      id="companyName"
                      name="companyName"
                      placeholder="Enter company name"
                      value={contactData.company || ""}
                      onChange={(e) => {
                        setContactData((prev) => ({
                          ...prev,
                          company: e.target.value,
                        }));
                      }}
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <Building
                            size={18}
                            style={{ marginRight: "8px", color: "#6b7280" }}
                          />
                        ),
                      }}
                    />
                  </div>

                  {/* Company Website - Always Editable with TextField */}
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Company Website{" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      id="companyWebsite"
                      name="companyWebsite"
                      type="url"
                      placeholder="Enter company website (e.g., https://example.com)"
                      value={contactData.website || ""}
                      onChange={(e) => {
                        setContactData((prev) => ({
                          ...prev,
                          website: e.target.value,
                        }));
                      }}
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <Globe
                            size={18}
                            style={{ marginRight: "8px", color: "#6b7280" }}
                          />
                        ),
                      }}
                    />
                  </div>

                  {/* Email - Always Editable with TextField */}
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Email Address <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      id="emailAddress"
                      name="emailAddress"
                      type="email"
                      placeholder="Enter email address"
                      value={contactData.email || ""}
                      onChange={(e) => {
                        setContactData((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }));
                      }}
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <Mail
                            size={18}
                            style={{ marginRight: "8px", color: "#6b7280" }}
                          />
                        ),
                      }}
                    />
                  </div>

                  {/* Client Location - Always Editable with TextField */}
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Client Location{" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      id="clientLocation"
                      name="clientLocation"
                      placeholder="Enter location (City, State, Country)"
                      value={(() => {
                        const locationParts = [
                          contactData.city,
                          contactData.state,
                          contactData.country,
                        ].filter(Boolean);
                        return locationParts.length > 0
                          ? locationParts.join(", ")
                          : "";
                      })()}
                      onChange={(e) => {
                        // Parse location string and update individual fields
                        const parts = e.target.value
                          .split(",")
                          .map((part) => part.trim());

                        setContactData((prev) => ({
                          ...prev,
                          city: parts[0] || "",
                          state: parts[1] || "",
                          country: parts[2] || "",
                        }));
                      }}
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <MapPin
                            size={18}
                            style={{ marginRight: "8px", color: "#6b7280" }}
                          />
                        ),
                      }}
                    />
                  </div>

                  {/* HubSpot Link - Auto Field (Not Editable) */}
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      HubSpot Contact Link (Auto-populated)
                    </Typography>
                    <TextField
                      id="hubspotLink"
                      name="hubspotLink"
                      placeholder="HubSpot Contact URL"
                      value={contactData.hubspotContactUrl}
                      disabled
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <Link
                            size={18}
                            style={{ marginRight: "8px", color: "#6b7280" }}
                          />
                        ),
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Project Information Section */}
            <div className="form-section enhanced">
              <div className="section-header">
                <div className="section-icon">
                  <Briefcase size={20} />
                </div>
                <div>
                  <h3>Project Information</h3>
                  <p>Tell us about the project</p>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Project Title <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <TextField
                    id="projectTitle"
                    name="projectTitle"
                    placeholder="Enter your project title (company name will be added automatically)"
                    value={formData.projectTitle}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!getFieldError("projectTitle")}
                    helperText={
                      getFieldError("projectTitle") ||
                      `Project title will automatically include " - ${
                        contactData.company || "Company Name"
                      }" as suffix`
                    }
                    required
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </div>

                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Solutions & Services{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <TextField
                    id="solutionsServices"
                    name="solutionsServices"
                    placeholder="Describe solutions and services needed"
                    value={formData.solutionsServices}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!getFieldError("solutionsServices")}
                    helperText={
                      getFieldError("solutionsServices") ||
                      (formData.solutionsServices ? "" : "")
                    }
                    required
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </div>
              </div>
            </div>

            {/* Basic Information Section */}
            <div className="form-section enhanced">
              <div className="section-header">
                <div className="section-icon">
                  <User size={20} />
                </div>
                <div>
                  <h3>Basic Information</h3>
                  <p>Tell us about the position you need to fill</p>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Position Title <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <TextField
                    id="positionTitle"
                    name="positionTitle"
                    placeholder="e.g., Virtual Assistant, Software Developer"
                    value={formData.positionTitle}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!getFieldError("positionTitle")}
                    helperText={getFieldError("positionTitle")}
                    required
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </div>

                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Assignment Type <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <FormControl
                    fullWidth
                    required
                    error={!!getFieldError("assignmentType")}
                  >
                    <InputLabel id="engagementType-label">
                      Select Assignment Type
                    </InputLabel>
                    <Select
                      labelId="assignmentType-label"
                      id="assignmentType"
                      name="assignmentType"
                      value={formData.assignmentType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Assignment Type"
                      size="small"
                    >
                      <MenuItem value="">Select assignment type</MenuItem>
                      {/* Only dynamic options from HubSpot API */}
                      {dynamicProperties.assignment_type?.options?.map(
                        (option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        )
                      )}
                    </Select>
                    {getFieldError("engagementType") && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.5 }}
                      >
                        {getFieldError("engagementType")}
                      </Typography>
                    )}
                  </FormControl>
                </div>
              </div>

              <div className="form-grid-small">
                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Contract Duration{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <FormControl
                    fullWidth
                    required
                    error={!!getFieldError("contractDuration")}
                  >
                    <InputLabel id="contractDuration-label">
                      Select Duration
                    </InputLabel>
                    <Select
                      labelId="contractDuration-label"
                      id="contractDuration"
                      name="contractDuration"
                      value={formData.contractDuration}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Contract Duration"
                      size="small"
                    >
                      <MenuItem value="">Select duration</MenuItem>
                      {/* Only dynamic options from HubSpot API */}
                      {dynamicProperties.contract_duration?.options?.map(
                        (option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        )
                      )}
                    </Select>
                    {getFieldError("contractDuration") && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.5 }}
                      >
                        {getFieldError("contractDuration")}
                      </Typography>
                    )}
                  </FormControl>
                </div>

                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Number of Resources{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <FormControl
                    fullWidth
                    required
                    error={!!getFieldError("numberOfResources")}
                  >
                    <InputLabel id="numberOfResources-label">
                      Select Number of Resources
                    </InputLabel>
                    <Select
                      labelId="numberOfResources-label"
                      id="numberOfResources"
                      name="numberOfResources"
                      value={formData.numberOfResources}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Select Number of Resources"
                      size="small"
                    >
                      <MenuItem value="">Choose number of resources</MenuItem>
                      {/* Only dynamic options from HubSpot API */}
                      {dynamicProperties.number_of_resources?.options?.map(
                        (option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        )
                      )}
                    </Select>
                    {getFieldError("numberOfResources") && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.5 }}
                      >
                        {getFieldError("numberOfResources")}
                      </Typography>
                    )}
                  </FormControl>
                </div>

                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Monthly Budget (USD){" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <TextField
                    id="monthlyPrice"
                    name="monthlyPrice"
                    type="number"
                    placeholder="Enter your budget"
                    value={formData.monthlyPrice}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!getFieldError("monthlyPrice")}
                    helperText={
                      getFieldError("monthlyPrice") || "Minimum budget: $100"
                    }
                    required
                    fullWidth
                    size="small"
                    inputProps={{ min: 100 }}
                    InputProps={{
                      startAdornment: (
                        <DollarSign
                          size={18}
                          style={{ marginRight: "8px", color: "#6b7280" }}
                        />
                      ),
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Skills & Requirements Section */}
            <div className="form-section enhanced">
              <div className="section-header">
                <div className="section-icon">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <h3>Skills & Requirements</h3>
                  <p>
                    Define the technical skills, qualifications, and expertise
                    needed for this role
                  </p>
                </div>
              </div>

              {/* First Row: Required Skills and Nice-to-Have Skills */}
              <div className="form-grid">
                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Required Skills <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <TextField
                    id="requiredSkills"
                    name="requiredSkills"
                    placeholder="e.g., JavaScript, React, Node.js, 3+ years experience, Bachelor's degree in Computer Science"
                    multiline
                    rows={4}
                    value={formData.requiredSkills}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!getFieldError("requiredSkills")}
                    helperText={
                      getFieldError("requiredSkills") ||
                      "List must-have technical skills, certifications, education requirements, and minimum experience"
                    }
                    required
                    fullWidth
                    size="small"
                    className="multiline"
                  />
                </div>

                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Nice-to-Have Skills (Optional)
                  </Typography>
                  <TextField
                    id="optionalSkills"
                    name="optionalSkills"
                    multiline
                    rows={4}
                    placeholder="e.g., AWS experience, GraphQL, TypeScript, Agile methodology, previous startup experience"
                    value={formData.optionalSkills}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    size="small"
                    className="multiline"
                    helperText="Additional skills that would be beneficial but not mandatory"
                  />
                </div>
              </div>

              {/* Second Row: Experience Level and Communication Requirements */}
              <div className="form-grid">
                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Experience Level <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <FormControl
                    fullWidth
                    required
                    error={!!getFieldError("experienceLevel")}
                  >
                    <InputLabel id="experienceLevel-label">
                      Select Required Experience Level
                    </InputLabel>
                    <Select
                      labelId="experienceLevel-label"
                      id="experienceLevel"
                      name="experienceLevel"
                      value={formData.experienceLevel}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Select Required Experience Level"
                      size="small"
                    >
                      {loadingProperties ? (
                        <MenuItem disabled>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Loading options...
                        </MenuItem>
                      ) : (
                        dynamicDropdowns.experience_level?.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {getFieldError("experienceLevel") && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.5 }}
                      >
                        {getFieldError("experienceLevel")}
                      </Typography>
                    )}
                  </FormControl>
                </div>

                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Communication Requirements{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <FormControl
                    fullWidth
                    required
                    error={!!getFieldError("levelOfCommunication")}
                  >
                    <InputLabel id="levelOfCommunication-label">
                      Select Communication Level
                    </InputLabel>
                    <Select
                      labelId="levelOfCommunication-label"
                      id="levelOfCommunication"
                      name="levelOfCommunication"
                      value={formData.levelOfCommunication}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Select Communication Level"
                      size="small"
                    >
                      <MenuItem value="">Select Communication Level</MenuItem>
                      {loadingProperties ? (
                        <MenuItem disabled>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Loading options...
                        </MenuItem>
                      ) : (
                        dynamicDropdowns.communication_requirements?.map(
                          (option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          )
                        )
                      )}
                    </Select>
                    {getFieldError("levelOfCommunication") && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.5 }}
                      >
                        {getFieldError("levelOfCommunication")}
                      </Typography>
                    )}
                  </FormControl>
                </div>
              </div>
            </div>

            {/* Work Arrangement Section */}
            <div className="form-section enhanced">
              <div className="section-header">
                <div className="section-icon">
                  <MapPin size={20} />
                </div>
                <div>
                  <h3>Work Arrangement</h3>
                  <p>
                    Define work location, schedule, and timing requirements for
                    the position
                  </p>
                </div>
              </div>

              {/* Single Row: Work Location, Shift Timing, and Close Date */}
              <div className="form-grid-small">
                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Work Location <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <FormControl
                    fullWidth
                    required
                    error={!!getFieldError("workLocation")}
                  >
                    <InputLabel id="workLocation-label">
                      Select Work Location Type
                    </InputLabel>
                    <Select
                      labelId="workLocation-label"
                      id="workLocation"
                      name="workLocation"
                      value={formData.workLocation}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Select Work Location Type"
                      size="small"
                    >
                      {loadingProperties ? (
                        <MenuItem disabled>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Loading options...
                        </MenuItem>
                      ) : (
                        dynamicDropdowns.work_location?.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </div>

                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Shift Timing <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <FormControl
                    fullWidth
                    required
                    error={!!getFieldError("shiftTiming")}
                  >
                    <InputLabel id="shiftTiming-label">
                      Select Working Hours
                    </InputLabel>
                    <Select
                      labelId="shiftTiming-label"
                      id="shiftTiming"
                      name="shiftTiming"
                      value={formData.shiftTiming}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Select Working Hours"
                      size="small"
                    >
                      {loadingProperties ? (
                        <MenuItem disabled>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Loading options...
                        </MenuItem>
                      ) : (
                        dynamicDropdowns.shift_timing?.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {getFieldError("shiftTiming") && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.5 }}
                      >
                        {getFieldError("shiftTiming")}
                      </Typography>
                    )}
                  </FormControl>
                </div>

                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Expected Joining Date{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={formatDateForDisplay(formData.expectedJoiningDate)}
                      onChange={(newValue) => {
                        const event = {
                          target: {
                            name: "expectedJoiningDate",
                            value: formatDateForStorage(newValue),
                          },
                        };
                        handleChange(event);
                      }}
                      format="MM/DD/YYYY"
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: !!getFieldError("expectedJoiningDate"),
                          helperText:
                            getFieldError("expectedJoiningDate") ||
                            "Click to open calendar and select the Expected Joining Date.",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </div>
            </div>

            {/* Interview Process Section - Only show for new deals */}

            <div className="form-section enhanced">
              <div className="section-header">
                <div className="section-icon">
                  <MessageCircle size={20} />
                </div>
                <div>
                  <h3>Interview Process</h3>
                  <p>
                    Define your hiring process, interview preferences, and
                    candidate evaluation criteria
                  </p>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                      marginBottom: "14px",
                    }}
                  >
                    Client Interview Required{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <FormControl
                    fullWidth
                    required
                    error={!!getFieldError("clientInterview")}
                  >
                    <InputLabel id="clientInterview-label">
                      Do you want to interview candidates?
                    </InputLabel>
                    <Select
                      labelId="clientInterview-label"
                      id="clientInterview"
                      name="clientInterview"
                      value={formData.clientInterview}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Do you want to interview candidates?"
                      size="small"
                      style={{ paddingTop: "14px", paddingBottom: "14px" }}
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>

                    {getFieldError("clientInterview") && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.5 }}
                      >
                        {getFieldError("clientInterview")}
                      </Typography>
                    )}
                  </FormControl>
                </div>

                <div className="form-field" style={{ padding: 0 }}>
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Interview Process Details{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <TextField
                    id="interviewProcess"
                    name="interviewProcess"
                    placeholder="e.g., 1-hour technical interview via video call, coding assessment, portfolio review, team fit discussion"
                    multiline
                    minRows={1}
                    maxRows={10}
                    value={formData.interviewProcess}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!getFieldError("interviewProcess")}
                    helperText={
                      getFieldError("interviewProcess") ||
                      "Describe your interview format, duration, assessment criteria, and any specific requirements"
                    }
                    required
                    fullWidth
                    size="small"
                    className="multiline interview-process"
                  />
                </div>
              </div>
            </div>

            {
              <div className="form-section enhanced">
                <div className="section-header">
                  <div className="section-icon">
                    <FileText size={20} />
                  </div>
                  <div>
                    <h3>Job Description</h3>
                    <p>
                      Provide a comprehensive description of the role,
                      responsibilities, and what success looks like
                    </p>
                  </div>
                </div>

                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Detailed Job Description{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>

                  <TextField
                    id="jobDescription"
                    name="jobDescription"
                    placeholder="Example Structure:
            Role Overview: Describe the position and its importance to your team

            Key Responsibilities:
            • Primary daily tasks and duties
            • Project ownership and deliverables
            • Collaboration requirements
            • Quality and performance standards

            What You'll Be Working On:
            • Specific projects or initiatives
            • Technologies and tools used
            • Team structure and reporting

            Success Metrics:
            • How performance will be measured
            • Key goals and objectives
            • Growth opportunities

            Example: We're looking for a Senior React Developer to lead our front-end development team. You'll be responsible for building scalable web applications, mentoring junior developers, and collaborating with our design and product teams..."
                    multiline
                    rows={6}
                    value={formData.jobDescription}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!getFieldError("jobDescription")}
                    helperText={
                      getFieldError("jobDescription") ||
                      "Provide detailed job description (text or upload PDF file below)"
                    }
                    fullWidth
                    size="small"
                    className="multiline"
                    sx={{ mb: 2 }}
                  />

                  {/* File Upload Drag & Drop Area */}
                  <Box
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                      border: `2px dashed ${isDragging ? "#2196f3" : "#ccc"}`,
                      borderRadius: 2,
                      padding: 3,
                      textAlign: "center",
                      backgroundColor: isDragging ? "#e3f2fd" : "#fafafa",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                        borderColor: "#999",
                      },
                    }}
                    onClick={() =>
                      document
                        .getElementById("job-description-file-input")
                        .click()
                    }
                  >
                    <input
                      id="job-description-file-input"
                      type="file"
                      accept=".pdf"
                      style={{ display: "none" }}
                      onChange={handleFileSelect}
                    />

                    {jobDescriptionFile ? (
                      <Box>
                        <FileText
                          size={40}
                          color="#4caf50"
                          style={{ marginBottom: 8 }}
                        />
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: "#4caf50" }}
                        >
                          {jobDescriptionFile.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ display: "block", mt: 1 }}
                        >
                          {(jobDescriptionFile.size / 1024 / 1024).toFixed(2)}{" "}
                          MB
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile();
                          }}
                          sx={{ mt: 2 }}
                        >
                          Remove File
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        <FileText
                          size={40}
                          color="#999"
                          style={{ marginBottom: 8 }}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Drag & drop PDF here or click to browse
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          PDF files only, max 10MB
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {fileUploadError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 1 }}
                    >
                      {fileUploadError}
                    </Typography>
                  )}
                </div>
              </div>
            }

            {/* Additional Comments Section */}
            <div className="form-section enhanced">
              <div className="section-header">
                <div className="section-icon">
                  <MessageCircle size={20} />
                </div>
                <div>
                  <h3>Additional Comments</h3>
                  <p>
                    Any additional information or special requirements
                    (Optional)
                  </p>
                </div>
              </div>

              <div className="form-field">
                <Typography
                  variant="subtitle2"
                  component="label"
                  sx={{
                    mb: 1,
                    display: "block",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  Additional Comments (Optional)
                </Typography>
                <TextField
                  id="additionalComments"
                  name="additionalComments"
                  placeholder="Enter any additional information, special requirements, or notes about this resource request..."
                  value={formData.additionalComments}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  className="multiline"
                  helperText="This field is optional. Use it to provide any extra context or requirements."
                />
              </div>
            </div>

            {/* Compact Missing Fields Summary - Always visible if there are missing fields */}
            {(() => {
              const summary = getValidationSummary();
              return (
                summary.missing.length > 0 && (
                  <div
                    style={{
                      background: "#fee2e2",
                      border: "1px solid #fca5a5",
                      borderRadius: "6px",
                      padding: "12px",
                      margin: "16px 0",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#dc2626",
                        fontWeight: 600,
                        marginBottom: "6px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <FileText size={16} />
                      Please complete {summary.missing.length} missing required
                      field{summary.missing.length > 1 ? "s" : ""}:
                    </Typography>

                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                    >
                      {summary.missing.map((field, index) => (
                        <span
                          key={index}
                          style={{
                            backgroundColor: "#dc2626",
                            color: "white",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                        >
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              );
            })()}

            {/* Expanded Validation Summary */}
            {showValidationSummary && (
              <div
                style={{
                  background: "#fff3cd",
                  border: "1px solid #ffeaa7",
                  borderRadius: "8px",
                  padding: "16px",
                  margin: "16px 0",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "#856404",
                    fontWeight: 600,
                    marginBottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FileText size={20} />
                  Form Completion Status
                </Typography>

                {(() => {
                  const summary = getValidationSummary();
                  return (
                    <div>
                      <Typography
                        variant="body2"
                        sx={{ color: "#856404", marginBottom: "8px" }}
                      >
                        <strong>Progress:</strong> {summary.filled} of{" "}
                        {summary.total} required fields completed
                      </Typography>

                      {summary.missing.length > 0 && (
                        <div style={{ marginBottom: "12px" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#d63384",
                              fontWeight: 600,
                              marginBottom: "4px",
                            }}
                          >
                            Missing Required Fields:
                          </Typography>
                          <ul
                            style={{
                              margin: "0",
                              paddingLeft: "20px",
                              color: "#d63384",
                            }}
                          >
                            {summary.missing.map((field, index) => (
                              <li key={index} style={{ fontSize: "14px" }}>
                                {field}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {summary.completed.length > 0 && (
                        <div>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#198754",
                              fontWeight: 600,
                              marginBottom: "4px",
                            }}
                          >
                            Completed Fields:
                          </Typography>
                          <ul
                            style={{
                              margin: "0",
                              paddingLeft: "20px",
                              color: "#198754",
                            }}
                          >
                            {summary.completed.map((field, index) => (
                              <li key={index} style={{ fontSize: "14px" }}>
                                {field}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div style={{ marginTop: "12px" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowValidationSummary(false)}
                          sx={{
                            color: "#856404",
                            borderColor: "#856404",
                            "&:hover": {
                              borderColor: "#856404",
                              backgroundColor: "rgba(133, 100, 4, 0.04)",
                            },
                          }}
                        >
                          Dismiss
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Footer */}
            <div className="form-footer">
              <button
                type="submit"
                className={`form-submit-btn ${
                  loading || !isFormValid ? "disabled" : ""
                }`}
                disabled={loading || !isFormValid}
              >
                <div className="btn-shine" />

                <div className="btn-content">
                  {loading ? (
                    <>
                      <Loader className="btn-spinner" size={20} />
                      <span>Submitting Request...</span>
                    </>
                  ) : (
                    <>
                      <Send size={20} className="btn-icon" />
                      <span>
                        {selectedDeal ? "Send Request" : "Send Request"}
                      </span>
                    </>
                  )}
                </div>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResourceForm;
